import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Check, Zap, CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import CheckoutModal from '@/components/CheckoutModal';
import { money, LEAD_PRICE, planBillingSuffix, planTermMonths, planTermNote } from '@/data/constants';

import { BILLING_POLICY, BILLING_POLICY_HEADING, bi, biList } from '@/data/legal';

interface Plan {
  id: string;
  code: string;
  name_en: string;
  name_es: string;
  price: number;
  included_leads: number;
  features_en: string[];
  features_es: string[];
}

const Pricing: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [active, setActive] = useState<Plan | null>(null);
  const [current, setCurrent] = useState<string>('');
  const [flash, setFlash] = useState('');
  const [pending, setPending] = useState(false);
  const pollRef = useRef<number | null>(null);
  const leadRef = useRef<HTMLDivElement | null>(null);
  const deepLinked = useRef(false);

  useEffect(() => {
    supabase.from('mkt_plans').select('*').eq('active', true).order('position').then(({ data }) => setPlans((data || []) as Plan[]));
  }, []);



  const readCurrentPlan = async () => {
    if (!profile) return '';
    const { data } = await supabase
      .from('mkt_subscriptions')
      .select('plan_code')
      .eq('user_id', profile.id)
      .eq('status', 'active')
      .limit(1);
    const code = data && data[0] ? data[0].plan_code : '';
    setCurrent(code);
    return code;
  };

  useEffect(() => {
    readCurrentPlan();
  }, [profile?.id]);

  useEffect(() => {
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, []);

  const choose = (p: Plan) => {
    if (!profile) {
      navigate(`${path('/login')}?next=${encodeURIComponent(`${path('/pricing')}?plan=${p.code}`)}`);
      return;
    }
    setActive(p);
  };

  /**
   * DEEP LINK from the floating "Start Now" plan picker (PlansModal).
   *   ?plan=basic|pro|premium -> open that plan's checkout straight away
   *   ?plan=single|compare    -> scroll to the single-lead / plan comparison
   * Runs once per visit (deepLinked guard) and then strips the query param so
   * a refresh or a back-navigation does not reopen the modal unexpectedly.
   */
  useEffect(() => {
    const wanted = params.get('plan');
    if (!wanted || deepLinked.current) return;

    if (wanted === 'single' || wanted === 'compare') {
      deepLinked.current = true;
      leadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      params.delete('plan');
      setParams(params, { replace: true });
      return;
    }

    // Plan codes need the plan rows to have loaded first.
    if (!plans.length) return;
    const match = plans.find((p) => p.code === wanted);
    deepLinked.current = true;
    params.delete('plan');
    setParams(params, { replace: true });
    if (match) choose(match);
  }, [params, plans, profile?.id]);



  /**
   * Called when the buyer has been sent to hosted checkout.
   * NOTHING is activated here — the plan only turns on when the platform
   * receives and validates the payment webhook. We simply poll the account so
   * the page flips to "active" the moment that happens.
   */
  const watchForActivation = (plan: Plan) => {
    setActive(null);
    setPending(true);
    setFlash(t('pay.activationPending'));

    let ticks = 0;
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      ticks += 1;
      const code = await readCurrentPlan();
      if (code === plan.code) {
        await refreshProfile();
        setPending(false);
        setFlash(t('pay.activated'));
        if (pollRef.current) window.clearInterval(pollRef.current);
      } else if (ticks > 40) {
        setPending(false);
        if (pollRef.current) window.clearInterval(pollRef.current);
      }
    }, 5000);
  };


  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
      <Seo title={t('pricing.title')} description={t('pricing.sub')} />
      <div className="text-center">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t('pricing.title')}</h1>
        <p className="mx-auto mt-3 max-w-2xl text-slate-500">{t('pricing.sub')}</p>
      </div>

      {flash && (
        <p
          className={`mx-auto mt-6 flex max-w-xl items-center justify-center gap-2 rounded-xl px-4 py-3 text-center text-sm font-semibold ${
            pending ? 'bg-blue-50 text-blue-800' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {pending ? <Clock className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />} {flash}
        </p>
      )}


      <div
        ref={leadRef}
        className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:flex sm:items-center sm:justify-between"
      >

        <div>
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
            <Zap className="h-5 w-5 text-orange-500" /> {t('pricing.payPerLead')}
          </h2>
          <p className="mt-1 text-sm text-slate-500">{t('pricing.payPerLeadDesc')}</p>
        </div>
        <div className="mt-4 text-left sm:mt-0 sm:text-right">
          <p className="text-3xl font-extrabold text-slate-900">{money(LEAD_PRICE)}</p>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t('pricing.perLead')}</p>
        </div>
      </div>
      <p className="mt-3 text-center text-sm font-semibold text-slate-500">{t('pricing.fixedLeadNote')}</p>


      <h2 className="mt-12 text-2xl font-extrabold text-slate-900">{t('pricing.plans')}</h2>
      <div className="mt-6 grid gap-6 md:grid-cols-3">
        {plans.map((p, i) => {
          const features = lang === 'es' ? p.features_es : p.features_en;
          const isCurrent = current === p.code;
          return (
            <div
              key={p.id}
              className={`flex flex-col rounded-2xl border p-6 ${
                i === 1 ? 'border-orange-300 bg-orange-50/40 shadow-lg ring-1 ring-orange-200' : 'border-slate-200 bg-white'
              }`}
            >
              <h3 className="text-lg font-extrabold text-slate-900">{lang === 'es' ? p.name_es : p.name_en}</h3>
              <p className="mt-2">
                <span className="text-3xl font-extrabold text-slate-900">{money(p.price)}</span>
                <span className="text-sm font-semibold text-slate-400">{planBillingSuffix(p.code, lang)}</span>
              </p>
              {planTermMonths(p.code) > 1 && (
                <p className="mt-2 rounded-lg bg-slate-900/5 px-3 py-2 text-xs font-semibold leading-relaxed text-slate-700">
                  {planTermNote(p.code, p.price, lang)}
                </p>
              )}

              <ul className="mt-5 flex-1 space-y-2.5">
                {(features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choose(p)}
                disabled={isCurrent}
                className={`mt-6 w-full rounded-xl px-4 py-3.5 text-base font-bold ${
                  isCurrent ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {isCurrent ? t('pricing.currentPlan') : t('pricing.choosePlan')}
              </button>
            </div>
          );
        })}
      </div>

      <h2 className="mt-14 text-2xl font-extrabold text-slate-900">{t('pricing.howLeadsWork')}</h2>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        {['howLeads1', 'howLeads2', 'howLeads3'].map((k) => (
          <div key={k} className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-sm leading-relaxed text-slate-600">{t(`pricing.${k}`)}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6">
        <h2 className="text-lg font-extrabold text-slate-900">{bi(BILLING_POLICY_HEADING, lang)}</h2>
        <ul className="mt-3 space-y-2">
          {biList(BILLING_POLICY, lang).map((p) => (
            <li key={p.slice(0, 24)} className="flex items-start gap-2 text-sm leading-relaxed text-slate-600">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              {p}
            </li>
          ))}
        </ul>
        <Link to={path('/contact')} className="mt-4 inline-block text-sm font-bold text-orange-600 hover:underline">
          {t('pricing.contactSupport')}
        </Link>
      </div>

      <p className="mt-6 text-center text-sm font-semibold text-slate-500">{t('pricing.freeNote')}</p>


      {active && (
        <CheckoutModal
          open={!!active}
          onClose={() => setActive(null)}
          amount={active.price}
          title={t('pay.subscribeTitle')}
          subtitle={lang === 'es' ? active.name_es : active.name_en}
          lineItem={`${lang === 'es' ? active.name_es : active.name_en} — ${active.included_leads} ${t('dash.includedLeads')}`}
          planCode={active.code}
          kind="subscription"
          onCheckoutOpened={() => watchForActivation(active)}
        />
      )}


    </div>
  );
};

export default Pricing;
